import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { COLORS } from '../constants';
import { BLOG_ARTICLES } from '../data/blog';
import Button from '../components/Button';
import SectionHeading from '../components/SectionHeading';


const BlogPage = () => {
  const [selectedArticle, setSelectedArticle] = useState(null);

  if (selectedArticle) {
    return (
      <section className="py-20 min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
        <div className="container mx-auto px-6 max-w-3xl">
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center gap-2 mb-8 text-[#7A6F60] hover:text-[#1E1E1C] font-bold transition-colors"
          >
            <ArrowLeft size={24} /> Back to Blog
          </button>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[3rem] overflow-hidden shadow-2xl"
          >
            <div className="relative h-96">
              <img
                src={selectedArticle.image}
                alt={selectedArticle.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-10 left-10 right-10 text-white">
                <span
                  className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-4"
                  style={{ backgroundColor: COLORS.accent, color: COLORS.secondaryBg }}
                >
                  {selectedArticle.category}
                </span>
                <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
                  {selectedArticle.title}
                </h1>
                <div className="flex items-center gap-6 text-sm opacity-90">
                  <span>{selectedArticle.date}</span>
                  <span>•</span>
                  <span>{selectedArticle.readTime}</span>
                </div>
              </div>
            </div>

            <div className="p-12">
              <div
  className="prose prose-lg md:prose-xl max-w-none
             prose-h3:mt-12 prose-h3:mb-4 prose-h3:text-2xl prose-h3:font-bold
             prose-h4:mt-8 prose-h4:mb-3 prose-h4:text-xl prose-h4:font-semibold
             prose-p:leading-relaxed prose-p:mb-6
             prose-ul:mb-6 prose-li:mb-2
             prose-strong:text-[#1E1E1C]
             prose-hr:my-12
             prose-table:my-10 prose-th:bg-[#F5F1E8] prose-th:p-4 prose-td:p-4
             prose-tr:border-b prose-tr:border-[#E8E2D6]"
  style={{ color: COLORS.textPrimary }}
  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
/>


              <div className="mt-12 pt-8 border-t border-[#E0D8C8]">
                <h3 className="text-2xl font-bold mb-6" style={{ color: COLORS.secondaryBg }}>
                  Ready to experience the difference?
                </h3>
                <Button
                  variant="primary"
                  onClick={() => {
                    window.dispatchEvent(new Event("go-to-quote"));
                  }}
                  className="px-8 py-4"
                >
                  Request a Quote
                </Button>
              </div>
            </div>
          </motion.article>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 min-h-screen" style={{ backgroundColor: COLORS.lightSurface }}>
      <Helmet>
        <title>Golf Scooter Blog & Tips | Lectric Leisure</title>
	<link rel="canonical" href="https://lectricleisure.golf/blog" />
        <meta
          name="description"
          content="Expert advice on electric golf scooters, maintenance tips, buying guides, and golf course recommendations from Lectric Leisure."
        />
      </Helmet>

      <SectionHeading
        title="Golf Mobility Insights"
        subtitle="Expert tips, maintenance guides, and industry knowledge."
      />

      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid md:grid-cols-2 gap-10">
          {BLOG_ARTICLES.map((article, idx) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group"
              onClick={() => setSelectedArticle(article)}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                <span
                  className="absolute top-4 left-4 px-4 py-2 rounded-full text-sm font-bold"
                  style={{ backgroundColor: COLORS.accent, color: COLORS.secondaryBg }}
                >
                  {article.category}
                </span>
              </div>

              <div className="p-8">
                <div className="flex items-center gap-4 text-sm text-[#7A6F60] mb-4">
                  <span>{article.date}</span>
                  <span>•</span>
                  <span>{article.readTime}</span>
                </div>

                <h2
                  className="text-2xl font-bold mb-4 group-hover:text-[#C7A36A] transition-colors font-serif"
                  style={{ color: COLORS.secondaryBg }}
                >
                  {article.title}
                </h2>

                <p className="text-[#1E1E1C] opacity-80 leading-relaxed mb-6">
                  {article.excerpt}
                </p>

                <div className="flex items-center gap-2 text-[#C7A36A] font-bold group-hover:gap-4 transition-all">
                  Read Article <ChevronRight size={20} />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogPage;